/**
 * Rückmeldung des Kunden erfassen.
 *
 * Hieß früher „Anruf dokumentieren". Ein Kunde meldet sich aber auch per Mail
 * oder an der Tür – die Handlung nach dem Kanal zu benennen hätte über kurz
 * oder lang drei Knöpfe für dieselbe Sache bedeutet. Der Kanal ist jetzt eine
 * Angabe darin.
 *
 * Der einzige Weg, ein Angebot zu pausieren: Ergebnis „Wiedervorlage" verlangt
 * ein Pflichtdatum, danach ruht der Vorgang bis zu diesem Tag.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';
import { jetztUhrzeit } from '../hilfen/format';
import type { Angebot, AngebotAntwort } from '../typen';

type Ergebnis = 'beobachten' | 'wiedervorlage' | 'ueberarbeiten' | 'absage' | 'sonstiges';

/** Rhythmus der Vorbelegung, wenn kein Termin vereinbart wurde. */
const STANDARD_TAGE = 14;

/** Termin plus n Tage, gerechnet vom Serverdatum – nicht von der Systemuhr. */
function plusTage(iso: string, tage: number): string {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + tage);
  return d.toISOString().slice(0, 10);
}

/** Was passiert, in Klartext – damit niemand raten muss. */
const WIRKUNG: Record<Ergebnis, string> = {
  beobachten: 'Der Vorgang wandert nach „Läuft noch" und kommt an diesem Tag von selbst zurück.',
  wiedervorlage:
    'Der Vorgang verlässt „Meine Aufgaben" und ruht bis zu diesem Tag. Im Vertriebscockpit bleibt er sichtbar.',
  ueberarbeiten:
    'Der Vorgang geht mit deiner Notiz zurück an den Kalkulanten und steht wieder auf START. Er verlässt „Meine Aufgaben", bis das Angebot erneut versendet ist.',
  absage: 'Das Angebot wird auf „Abgesagt" gesetzt und ist damit abgeschlossen.',
  sonstiges: 'Der Vorgang wandert nach „Läuft noch" und kommt an diesem Tag von selbst zurück.',
};

/** Bei welchen Ergebnissen ein Rückkehrtermin erfasst wird. */
const MIT_TERMIN: Ergebnis[] = ['beobachten', 'wiedervorlage', 'sonstiges'];

export function AnrufModal({
  angebot,
  onSchliessen,
  onFertig,
}: {
  angebot: Angebot;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen } = useKora();
  const meta = useMeta();

  const [datum, setDatum] = useState(meta.heute);
  const [zeit, setZeit] = useState(jetztUhrzeit());
  const [notiz, setNotiz] = useState('');
  const [ergebnis, setErgebnis] = useState<Ergebnis>('beobachten');
  const [kanal, setKanal] = useState('telefon');
  const [wiedervorlage, setWiedervorlage] = useState('');
  const [sonst, setSonst] = useState('');
  const [fehler, setFehler] = useState('');

  /*
   * „Wieder auf meinem Tisch am".
   *
   * Hier und nur hier wird entschieden, wann ein Vorgang zurückkommt – niemand
   * soll sich anderswo eine Wiedervorlage merken müssen. Bei „beobachten" und
   * „sonstiges" vorbelegt, bei der Wiedervorlage bewusst leer und Pflicht: Ein
   * vorgeschlagener Termin würde dort einfach bestätigt, obwohl der Kunde einen
   * anderen genannt hat.
   */
  const [termin, setTermin] = useState(() => plusTage(meta.heute, STANDARD_TAGE));

  function ergebnisWaehlen(neu: Ergebnis) {
    setErgebnis(neu);
    setFehler('');
    if (neu === 'wiedervorlage') setTermin(wiedervorlage);
    else if (MIT_TERMIN.includes(neu) && !termin) setTermin(plusTage(meta.heute, STANDARD_TAGE));
  }

  function terminSetzen(wert: string) {
    setTermin(wert);
    if (ergebnis === 'wiedervorlage') setWiedervorlage(wert);
    setFehler('');
  }

  const mutation = useMutation({
    mutationFn: () =>
      api.sende<AngebotAntwort>(`/angebote/${angebot.id}/anruf`, {
        datum,
        zeit,
        notiz: notiz.trim(),
        ergebnis,
        kanal,
        wiedervorlage: ergebnis === 'wiedervorlage' ? termin : null,
        faellig: MIT_TERMIN.includes(ergebnis) ? termin : null,
        sonst: ergebnis === 'sonstiges' ? sonst.trim() : '',
      }),
    onSuccess: (antwort) => {
      onFertig();
      if (antwort.toast) toastZeigen(antwort.toast);
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  function speichern() {
    if (!notiz.trim()) {
      setFehler('Bitte eine kurze Gesprächsnotiz eintragen.');
      return;
    }
    if (MIT_TERMIN.includes(ergebnis) && !termin) {
      setFehler(
        ergebnis === 'wiedervorlage'
          ? 'Bitte den vereinbarten Termin eintragen.'
          : 'Bitte angeben, wann der Vorgang wieder auf deinen Tisch soll.',
      );
      return;
    }
    if (ergebnis === 'sonstiges' && !sonst.trim()) {
      setFehler('Bitte das Ergebnis in eigenen Worten beschreiben.');
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      titel="Rückmeldung erfassen"
      untertitel={`${angebot.name} · ${angebot.kunde.name} · ${angebot.kunde.telefon || 'keine Nummer hinterlegt'}`}
      breite={520}
      fest={mutation.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={mutation.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer onClick={speichern} disabled={mutation.isPending}>
            {mutation.isPending ? 'Wird gespeichert …' : 'Speichern'}
          </KnopfPrimaer>
        </>
      }
    >
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <label className="modal-feld" style={{ flex: 1, minWidth: 150 }}>
          <span>Datum</span>
          <input
            type="date"
            className="modal-eingabe"
            value={datum}
            onChange={(e) => {
              setDatum(e.target.value);
              setFehler('');
            }}
          />
        </label>
        <label className="modal-feld" style={{ width: 130 }}>
          <span>Uhrzeit</span>
          <input
            type="time"
            className="modal-eingabe"
            value={zeit}
            onChange={(e) => {
              setZeit(e.target.value);
              setFehler('');
            }}
          />
        </label>
      </div>

      {/* Der Kanal gehört zur Rückmeldung, nicht zum Vorgang. */}
      <div className="modal-feld">
        <span>Wie kam die Rückmeldung?</span>
        <div className="eh-chips">
          {[
            { key: 'telefon', label: 'Telefon' },
            { key: 'mail', label: 'E-Mail' },
            { key: 'persoenlich', label: 'Persönlich' },
            { key: 'intern', label: 'Intern entschieden' },
          ].map((k) => (
            <button
              key={k.key}
              type="button"
              className={`modal-radio ${kanal === k.key ? 'aktiv' : ''}`}
              onClick={() => setKanal(k.key)}
            >
              {k.label}
            </button>
          ))}
        </div>
      </div>

      <label className="modal-feld">
        <span>Gesprächsnotiz</span>
        <textarea
          className="modal-textarea"
          rows={4}
          placeholder="Was wurde besprochen?"
          value={notiz}
          onChange={(e) => {
            setNotiz(e.target.value);
            setFehler('');
          }}
        />
      </label>

      <div className="modal-feld">
        <span>Ergebnis</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {meta.anrufErgebnisse.map((o) => (
            <button
              key={o.key}
              type="button"
              className={`modal-radio ${ergebnis === o.key ? 'aktiv' : ''}`}
              onClick={() => {
                ergebnisWaehlen(o.key as Ergebnis);
              }}
            >
              <span className="modal-radio-punkt" />
              <span className="modal-radio-text">{o.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/*
        Der Rückkehrtermin steht direkt unter dem Ergebnis – dort, wo die
        Entscheidung fällt. Drei Schnellwahl-Chips für die üblichen Abstände,
        das Feld bleibt immer überschreibbar.
      */}
      {MIT_TERMIN.includes(ergebnis) && (
        <div className="modal-feld">
          <span>
            Wieder auf meinem Tisch am
            {ergebnis === 'wiedervorlage' ? ' · Pflichtfeld' : ''}
          </span>
          <div className="modal-termin">
            <input
              type="date"
              className={`modal-eingabe ${fehler && !termin ? 'modal-eingabe--fehler' : ''}`}
              value={termin}
              min={meta.heute}
              onChange={(e) => terminSetzen(e.target.value)}
            />
            <div className="eh-chips">
              {[
                { label: '+1 Woche', tage: 7 },
                { label: '+2 Wochen', tage: 14 },
                { label: '+1 Monat', tage: 30 },
              ].map((s) => (
                <button
                  key={s.tage}
                  type="button"
                  className={`modal-radio ${termin === plusTage(meta.heute, s.tage) ? 'aktiv' : ''}`}
                  onClick={() => terminSetzen(plusTage(meta.heute, s.tage))}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <span className="modal-hinweis">{WIRKUNG[ergebnis]}</span>
        </div>
      )}

      {!MIT_TERMIN.includes(ergebnis) && (
        <div className="modal-hinweis">{WIRKUNG[ergebnis]}</div>
      )}

      {ergebnis === 'sonstiges' && (
        <label className="modal-feld">
          <span>Ergebnis in eigenen Worten</span>
          <input
            className="modal-eingabe"
            placeholder="z. B. Angebot wird an Architekten weitergeleitet"
            value={sonst}
            onChange={(e) => {
              setSonst(e.target.value);
              setFehler('');
            }}
          />
        </label>
      )}


      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}
